import moment from 'moment';
import {
  generatePdfFromTemplate,
  PdfTemplateLayoutItem,
  GeneratePdfTemplateResult,
  PdfTemplateFontWeight,
} from './PdfTemplateService';

const FEEDBACK_PDF_URL =
  'https://arb-systems-s3-public.s3.amazonaws.com/public/Performance/PerformanceManagement_Feedback.pdf';

const formatDateValue = (value?: string) => {
  if (!value) {
    return '';
  }

  const parsed = moment(value);
  if (!parsed.isValid()) {
    return value;
  }

  return parsed.format('MM/DD/YYYY');
};

const extractFeedbackField = (data: any, candidates: string[]) => {
  for (const key of candidates) {
    const segments = key.split('.');
    let current = data;

    for (const segment of segments) {
      if (current && Object.prototype.hasOwnProperty.call(current, segment)) {
        current = current[segment];
      } else {
        current = undefined;
        break;
      }
    }

    if (current !== undefined && current !== null) {
      if (typeof current === 'string') {
        const trimmed = current.trim();
        if (trimmed !== '') {
          return trimmed;
        }
      }

      if (typeof current === 'number') {
        return current.toString();
      }

      if (current instanceof Date) {
        return moment(current).format('MM/DD/YYYY');
      }
    }
  }

  return '';
};

export const formatPolicyViolationText = (item: any) => {
  if (Array.isArray(item?.policyViolations) && item.policyViolations.length > 0) {
    const parts = item.policyViolations.map((pv: any) => (typeof pv === 'string' ? pv : pv?.name || pv?.key || ''));
    const joined = parts.filter((p: string) => p && String(p).trim() !== '').join(', ');
    if (joined) {
      return joined;
    }
  }
  return (
    extractFeedbackField(item, ['policyViolation.name', 'policyViolation', 'policy_violation', 'policy_violated']) || ''
  );
};

const formatImmediateFeedbackDisplay = (value: string) => {
  const t = String(value || '')
    .trim()
    .toLowerCase();
  if (t === 'yes') {
    return 'Yes';
  }
  if (t === 'no') {
    return 'No';
  }
  return value;
};

const sanitizeFileName = (text: string) => {
  if (!text) {
    return 'feedback';
  }

  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 80) || 'feedback'
  );
};

interface AddTextOptions {
  y?: number;
  x?: number;
  textColor?: string;
  page?: number;
  fontSize?: number;
  fontWeight?: PdfTemplateFontWeight;
  maxWidth?: number;
  lineHeight?: number;
  spacingAfter?: number;
}

const buildFeedbackLayout = (item: any, userLogged?: any, currentSchool?: any) => {
  const employeeName = extractFeedbackField(item, ['name', 'employeeName', 'employee_name', 'employee.name']) || '';
  const position = extractFeedbackField(item, ['position', 'employeePosition', 'jobTitle', 'job_title']) || '';
  const _supervisor =
    extractFeedbackField(item, ['supervisor', 'supervisor.name', 'supervisorName', 'supervisor_name']) || '';
  const dateOfIncident = formatDateValue(extractFeedbackField(item, ['date_of_incident', 'dateOfIncident', 'date']));
  const policyViolation = formatPolicyViolationText(item);
  const immediateFeedbackRaw = extractFeedbackField(item, ['immediate_feedback', 'immediateFeedback', 'immediate']);
  const immediateFeedback = formatImmediateFeedbackDisplay(immediateFeedbackRaw);
  const descriptionOfFeedback = extractFeedbackField(item, [
    'description_of_feedback',
    'descriptionOfFeedback',
    'description',
  ]);
  const expectationForImprovement = extractFeedbackField(item, [
    'expectation_for_improvement',
    'expectationForImprovement',
    'expectation',
  ]);
  const createdAt = formatDateValue(extractFeedbackField(item, ['created_at', 'createdAt']));

  const department = extractFeedbackField(item, ['department', 'department.name', 'departmentName', 'department_name']);

  const layout: PdfTemplateLayoutItem[] = [];
  const addText = (text: string, options: AddTextOptions = {}) => {
    const { x, ...rest } = options;
    const textItem: PdfTemplateLayoutItem = {
      type: 'text',
      text,
      x: x ?? 54,
      ...rest,
    };
    layout.push(textItem);
  };

  const addHr = (options: { x?: number; thickness?: number; color?: string; spacingAfter?: number } = {}) => {
    layout.push({
      type: 'hr',
      x: options.x ?? 54,
      thickness: options.thickness ?? 1,
      color: options.color,
      spacingAfter: options.spacingAfter ?? 12,
    });
  };

  const addSpacing = (height: number) => {
    layout.push({ type: 'spacing', height });
  };

  addText(`${currentSchool?.label ?? ''}`, {
    fontWeight: 'regular',
    fontSize: 16,
    textColor: '#fd9821',
    y: 711,
    x: 203,
  });

  const submittedByName = item?.submittedByUser
    ? `${item.submittedByUser.firstName ?? ''} ${item.submittedByUser.lastName ?? ''}`.trim()
    : '';

  addText(submittedByName, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 638,
    x: 160,
  });

  addText(`${createdAt}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 620,
    x: 160,
  });

  addText(`${employeeName}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 584,
    x: 110,
  });

  addText(`${dateOfIncident}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 584,
    x: 400,
  });

  addText(`${position}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 559,
    x: 120,
  });

  addText(`${department ?? ''}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 559,
    x: 355,
  });

  addSpacing(12);

  const sequentialX = 80;
  const sequentialFontSize = 12;

  const spacingAfterTitle = 10;
  const spacingAfterContent = 22;

  addText('Policy Violation:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addSpacing(5);
  addText(`${policyViolation}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addHr();
  addSpacing(10);

  addText('Description of Feedback:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addSpacing(5);
  addText(`${descriptionOfFeedback}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addHr();
  addSpacing(10);

  addText('Immediate Feedback:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addSpacing(5);
  addText(`${immediateFeedback}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addHr();
  addSpacing(10);

  addText('Expectation for Improvement:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addSpacing(5);
  addText(`${expectationForImprovement}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  // addField('Employee Name', employeeName);
  // addField('Position', position);
  // addField('Department', department);
  // addField('Supervisor', supervisor);
  // addField('Date of Incident', dateOfIncident);
  // addField('Date of Hire', dateOfHire);
  // addField('Policy Violation', policyViolation);
  // addField('Immediate Feedback', immediateFeedback, 18);
  // addField('Email Copy', emailOfFeedback);
  // addField('Description of Feedback', descriptionOfFeedback, 18);
  // addField('Expectation for Improvement', expectationForImprovement, 18);
  // addField('Created At', createdAt, 0);

  return {
    layout,
    employeeName,
  };
};

export interface FeedbackPdfResult extends GeneratePdfTemplateResult {
  fileName: string;
}

export const generateFeedbackPdf = async (
  item: any,
  userLogged?: any,
  currentSchool?: any,
): Promise<FeedbackPdfResult> => {
  const { layout, employeeName } = buildFeedbackLayout(item, userLogged, currentSchool);
  const result = await generatePdfFromTemplate({
    templatePath: FEEDBACK_PDF_URL,
    layout,
    cursorStart: 730,
  });

  return {
    ...result,
    fileName: `${sanitizeFileName(employeeName)}-feedback.pdf`,
  };
};
